import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Bell, BellDot, Check, X as XIcon, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Notifications Bell 🔔
 * - Header button with red unread-count badge
 * - Dropdown panel (lighter than full drawer) opens on click
 * - Polls /api/notifications/unread-count every 30s
 * - Click a notif → mark read + navigate to `link`
 * - "Mark all read" + per-item delete supported
 */
const KIND_DOT = {
  badge:         "bg-[#D4AF37]",
  dm:            "bg-blue-500",
  friend_req:    "bg-purple-500",
  friend_accept: "bg-emerald-500",
  loot:          "bg-pink-500",
  premium:       "bg-[#D4AF37]",
  streak_risk:   "bg-orange-500",
  merch_shipped: "bg-cyan-500",
  friend_share:  "bg-purple-500",
  friend_feed:   "bg-purple-500",
  system:        "bg-zinc-500",
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  const w = Math.floor(d / 7);
  return `${w}w`;
}

export default function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);
  // Track last-seen unread friend_feed ID so we toast only NEW ones (not on first load).
  const seenFriendFeedRef = useRef({ initialized: false, ids: new Set() });

  const fetchUnread = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/notifications/unread-count`);
      setUnread(Number(data?.unread_count) || 0);
    } catch {
      // silent
    }
  }, [user]);

  // Lightweight detector: every 30s, pull the latest unread `friend_feed`
  // AND `proxy_raid` notifs and surface a toast for any we haven't seen yet.
  // Skip on first poll so users don't get blasted with old notifs on refresh.
  const fetchFriendFeedToasts = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/notifications?limit=15&only_unread=true`);
      const items = data?.items || [];
      const toastable = items.filter((n) => n.kind === "friend_feed" || n.kind === "proxy_raid");
      const seen = seenFriendFeedRef.current;
      if (!seen.initialized) {
        // First poll — record IDs, don't toast.
        toastable.forEach((n) => seen.ids.add(n.id));
        seen.initialized = true;
        return;
      }
      // Surface anything new since the last poll
      for (const n of toastable) {
        if (!seen.ids.has(n.id)) {
          seen.ids.add(n.id);
          // 🦅 Proxy raid notifs route to /castle inbox
          if (n.kind === "proxy_raid") {
            const evType = n.meta?.event;
            const winning = n.meta?.won;
            const toastFn = (evType === "resolved" && !winning) ? toast.error : toast.success;
            toastFn(n.title || "🦅 Raid news!", {
              description: n.body || "",
              duration: 7000,
              action: {
                label: "Open Inbox",
                onClick: () => navigate(n.link || "/castle?open_inbox=1"),
              },
            });
          } else {
            toast.success(n.title || "🫐 A friend fed your bird!", {
              description: n.body || "",
              duration: 6000,
              action: {
                label: "Open Pet",
                onClick: () => navigate(n.link || "/pet"),
              },
            });
          }
        }
      }
    } catch {
      // silent
    }
  }, [user, navigate]);

  const fetchList = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/notifications?limit=25`);
      setItems(data?.items || []);
      setUnread(Number(data?.unread_count) || 0);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial fetch + 30s poll
  useEffect(() => {
    if (!user) return;
    fetchUnread();
    fetchFriendFeedToasts();
    const id = setInterval(() => {
      fetchUnread();
      fetchFriendFeedToasts();
    }, 30000);
    // 🔔 Feb 2026 — react to ProfileNotificationsFeed auto-mark so the
    // header bell badge clears the instant the profile checks items off,
    // instead of waiting up to 30s for the next poll.
    const onReadBatch = () => fetchUnread();
    window.addEventListener("omnimuscle:notifications-read", onReadBatch);
    return () => {
      clearInterval(id);
      window.removeEventListener("omnimuscle:notifications-read", onReadBatch);
    };
  }, [user, fetchUnread, fetchFriendFeedToasts]);

  // Load list when opening
  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  // Outside click + Escape to close
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function handleClickItem(n) {
    if (!n.read) {
      // optimistic: mark read locally
      setItems((cur) => cur.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      try {
        await axios.post(`${API}/notifications/${n.id}/read`);
      } catch {
        // ignore — re-fetch on next open
      }
    }
    setOpen(false);
    if (n.link) {
      // External link? navigate(). Hash-only fragment? Use window.location to scroll.
      if (n.link.startsWith("http")) {
        window.location.href = n.link;
      } else {
        navigate(n.link);
      }
    }
  }

  async function handleMarkAllRead(e) {
    e.stopPropagation();
    setItems((cur) => cur.map((x) => ({ ...x, read: true })));
    setUnread(0);
    try {
      await axios.post(`${API}/notifications/read-all`);
    } catch {
      // ignore
    }
  }

  async function handleDelete(e, id) {
    e.stopPropagation();
    setItems((cur) => cur.filter((x) => x.id !== id));
    try {
      await axios.delete(`${API}/notifications/${id}`);
    } catch {
      // ignore
    }
  }

  if (!user) return null;

  const Icon = unread > 0 ? BellDot : Bell;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        data-testid="notif-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative w-10 h-10 border border-zinc-800 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors flex items-center justify-center text-neutral-300"
        title={`${unread} unread notification${unread === 1 ? "" : "s"}`}
        aria-label={`Notifications, ${unread} unread`}
      >
        <Icon size={16} />
        {unread > 0 && (
          <span
            data-testid="notif-unread-count"
            className="absolute -top-1.5 -right-1.5 bg-[#DC2626] text-white text-[10px] font-bold tabular-nums min-w-[20px] h-5 px-1 flex items-center justify-center rounded-full border border-black leading-none"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          data-testid="notif-panel"
          className="absolute right-0 mt-2 w-[360px] max-w-[92vw] bg-[#0A0A0A] border-2 border-[#D4AF37] shadow-[0_8px_40px_rgba(0,0,0,0.85)] z-[80] flex flex-col"
          style={{ maxHeight: "min(560px, 70vh)" }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between shrink-0">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                Notifications
              </div>
              <div className="text-[11px] text-neutral-500 tabular-nums">
                {unread} unread · {items.length} total
              </div>
            </div>
            {unread > 0 && (
              <button
                type="button"
                data-testid="notif-mark-all-read"
                onClick={handleMarkAllRead}
                className="text-[10px] uppercase tracking-widest text-[#D4AF37] hover:text-white font-bold inline-flex items-center gap-1"
              >
                <Check size={12} /> Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="py-10 text-center text-neutral-500 text-sm">
                <Loader2 className="animate-spin inline mr-1.5" size={13} /> Loading…
              </div>
            ) : items.length === 0 ? (
              <EmptyState />
            ) : (
              items.map((n) => (
                <NotifRow
                  key={n.id}
                  n={n}
                  onClick={() => handleClickItem(n)}
                  onDelete={(e) => handleDelete(e, n.id)}
                />
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-zinc-800 text-center shrink-0 space-y-1.5">
            <button
              type="button"
              onClick={() => { setOpen(false); navigate("/notifications"); }}
              data-testid="notif-see-all"
              className="w-full text-[11px] uppercase tracking-widest text-[#D4AF37] hover:text-white font-bold"
            >
              See all notifications →
            </button>
            <p className="text-[10px] text-neutral-600 uppercase tracking-widest">
              Read notifs auto-clear after 7 days
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function NotifRow({ n, onClick, onDelete }) {
  const dotCls = KIND_DOT[n.kind] || "bg-zinc-500";
  return (
    <button
      type="button"
      data-testid={`notif-item-${n.id}`}
      onClick={onClick}
      className={`relative w-full text-left p-3 border-b border-zinc-900 transition-colors group flex gap-3 ${
        n.read ? "opacity-60 hover:bg-zinc-900/40" : "bg-[#0F0F0F] hover:bg-zinc-900/60"
      }`}
    >
      {/* Unread indicator dot */}
      <div className="shrink-0 pt-1.5">
        {!n.read ? (
          <div className={`w-2 h-2 rounded-full ${dotCls}`} />
        ) : (
          <div className="w-2 h-2" />
        )}
      </div>

      <div className="text-2xl leading-none shrink-0 mt-0.5" aria-hidden="true">
        {n.icon || "🔔"}
      </div>

      <div className="flex-1 min-w-0">
        <div className={`text-sm leading-snug ${n.read ? "text-neutral-400" : "text-white font-bold"} line-clamp-2`}>
          {n.title}
        </div>
        {n.body && (
          <div className="text-xs text-neutral-500 mt-0.5 line-clamp-2 leading-snug">
            {n.body}
          </div>
        )}
        <div className="text-[10px] uppercase tracking-widest text-neutral-600 mt-1 tabular-nums">
          {timeAgo(n.created_at)}
        </div>
      </div>

      <button
        type="button"
        onClick={onDelete}
        data-testid={`notif-delete-${n.id}`}
        aria-label="Delete notification"
        className="shrink-0 opacity-0 group-hover:opacity-100 text-neutral-600 hover:text-red-400 transition-opacity p-1 self-start"
      >
        <XIcon size={13} />
      </button>
    </button>
  );
}

function EmptyState() {
  return (
    <div className="py-12 text-center px-4" data-testid="notif-empty">
      <div className="text-5xl mb-2 opacity-40">🪶</div>
      <p className="text-sm text-neutral-400 font-display">All clear.</p>
      <p className="text-xs text-neutral-600 mt-1 max-w-[240px] mx-auto leading-relaxed">
        Win badges, get DMs, or hatch a Mega Jackpot — they'll show up here.
      </p>
    </div>
  );
}
